import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { useAuth } from '@shared/hooks/useAuth';
import type { AuthState } from '@shared/types/auth';

interface AuthContextValue extends AuthState {
  error: string | null;
  signInWithPassword: (email: string, password: string) => Promise<unknown>;
  signInWithMagicLink: (email: string) => Promise<unknown>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<unknown>;
}

const INITIAL_STATE: AuthContextValue = {
  user: null,
  authContext: null,
  capabilities: [],
  isAuthenticated: false,
  isLoading: true,
  error: null,
  signInWithPassword: async () => {
    throw new Error('AuthProvider not registered');
  },
  signInWithMagicLink: async () => {
    throw new Error('AuthProvider not registered');
  },
  signOut: async () => {
    throw new Error('AuthProvider not registered');
  },
  refreshSession: async () => {
    throw new Error('AuthProvider not registered');
  },
};

const AuthContext = createContext<AuthContextValue>(INITIAL_STATE);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();

  const value = useMemo<AuthContextValue>(() => ({
    user: auth.user,
    authContext: auth.authContext,
    capabilities: auth.capabilities,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: null,
    signInWithPassword: async (email, password) => auth.signInWithPassword(email, password),
    signInWithMagicLink: async (email) => auth.signInWithMagicLink(email),
    signOut: auth.signOut,
    refreshSession: async () => auth.refreshSession(),
  }), [auth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
